import React, { useState, useRef } from "react";
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import { generateEContractPdf } from '../utils/generateEContractPdf';
import { saveAs } from 'file-saver';
import 'pdfjs-dist/webpack';
// This import ensures the worker is bundled correctly by Vite/Webpack
// pdfjs.GlobalWorkerOptions.workerSrc is set automatically by 'pdfjs-dist/webpack'

// SignaturePad: Draws signature on canvas and exports as base64 PNG
function SignaturePad({ value, onChange, disabled }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);

  // Draw logic
  const startDrawing = (e) => {
    if (disabled) return;
    setDrawing(true);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    const { x, y } = getRelativePos(e);
    ctx.moveTo(x, y);
  };
  const draw = (e) => {
    if (!drawing || disabled) return;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getRelativePos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const stopDrawing = () => {
    if (drawing) {
      setDrawing(false);
      // Export base64
      if (onChange) onChange(canvasRef.current.toDataURL("image/png"));
    }
  };
  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (onChange) onChange("");
  };
  const getRelativePos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const touch = e.touches?.[0];
    const clientX = touch ? touch.clientX : e.clientX;
    const clientY = touch ? touch.clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };
  return (
    <div style={{ border: "1px solid #ccc", borderRadius: 8, padding: 8, background: "#fafbfc" }}>
      <canvas
        ref={canvasRef}
        width={350}
        height={100}
        style={{ touchAction: "none", background: "#fff", borderRadius: 4, width: "100%", height: 100, border: "1px solid #eee", cursor: disabled ? "not-allowed" : "crosshair" }}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
        disabled={disabled}
      />
      <div style={{ marginTop: 6, display: "flex", justifyContent: "space-between" }}>
        <button type="button" onClick={clear} disabled={disabled} style={{ fontSize: 12, padding: "2px 8px" }}>Clear</button>
        <span style={{ fontSize: 12, color: "#888" }}>Draw signature above</span>
      </div>
    </div>
  );
}

// Main E-Contract Sign Page
export default function EContractSignPage({ groupId, listingId, providerId, groupName, providerName, onSigned }) {
  // Inputs
  const [signatoryName, setSignatoryName] = useState("");
  const [signatoryRole, setSignatoryRole] = useState("");
  const [signature, setSignature] = useState("");

  // State
  const [previewUrl, setPreviewUrl] = useState(null);
  const [signedUrl, setSignedUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState(null);

  // Role tag selector
  const ROLE_OPTIONS = ["CEO", "COO", "Admin"];

  // Preview contract PDF (unsigned)
  const handlePreview = async () => {
    setError(null);
    setLoading(true);
    setPreviewUrl(null);
    try {
      const pdfBytes = await generateEContractPdf({
        groupName,
        providerName,
        listingId,
        signatoryName: signatoryName.trim(),
        signatoryRole: signatoryRole.trim(),
        signatureImage: null
      });
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (e) {
      setError('Failed to generate contract PDF.');
    }
    setLoading(false);
  };

  // Sign & submit contract (generate PDF with signature)
  const handleSign = async () => {
    setError(null);
    setSigning(true);
    setSignedUrl(null);
    try {
      const pdfBytes = await generateEContractPdf({
        groupName,
        providerName,
        listingId,
        signatoryName: signatoryName.trim(),
        signatoryRole: signatoryRole.trim(),
        signatureImage: signature
      });
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setSignedUrl(url);
      // Optionally trigger download automatically:
      // saveAs(blob, `RxRooster-Contract-${groupName}.pdf`);
      if (onSigned) onSigned(url);
    } catch (e) {
      setError('Failed to generate signed contract PDF.');
    }
    setSigning(false);
  };

  // Validation
  const canPreview = signatoryName.trim() && signatoryRole.trim();
  const canSign = canPreview && signature;

  return (
    <div style={{ maxWidth: 460, margin: "40px auto", background: "#fff", borderRadius: 12, boxShadow: "0 2px 16px #0001", padding: 28, fontFamily: "Inter, sans-serif" }}>
      <h2 style={{ marginBottom: 12 }}>RxRooster E-Contract</h2>
      <div style={{ fontSize: 15, color: "#444", marginBottom: 18 }}>
        This contract binds <b>{groupName || "[Group]"}</b> to pay <b>RxRooster</b> $25,000 if you hire <b>{providerName || "[Provider]"}</b> via listing <b>{listingId || "[ID]"}</b> within 12 months of the listing date.
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontWeight: 500 }}>Signatory Name</label>
        <input
          type="text"
          className="form-control"
          style={{ width: "100%", marginTop: 4, marginBottom: 10, padding: 8, borderRadius: 6, border: "1px solid #ddd" }}
          value={signatoryName}
          onChange={e => setSignatoryName(e.target.value)}
          placeholder="Full name"
          autoComplete="off"
        />
        <label style={{ fontWeight: 500 }}>Signatory Role</label>
        <div style={{ display: "flex", gap: 8, marginTop: 4, marginBottom: 10 }}>
          {ROLE_OPTIONS.map(role => (
            <button
              key={role}
              type="button"
              style={{
                padding: "6px 14px",
                borderRadius: 16,
                border: role === signatoryRole ? "2px solid #1976d2" : "1px solid #bbb",
                background: role === signatoryRole ? "#e3f2fd" : "#f7f7f7",
                color: role === signatoryRole ? "#1976d2" : "#444",
                fontWeight: 500,
                cursor: "pointer"
              }}
              onClick={() => setSignatoryRole(role)}
            >
              {role}
            </button>
          ))}
        </div>
      </div>
      <div style={{ marginBottom: 18 }}>
        <label style={{ fontWeight: 500 }}>Signature</label>
        <SignaturePad value={signature} onChange={setSignature} disabled={signing} />
      </div>
      <div style={{ display: "flex", gap: 10, marginBottom: 18 }}>
        <button
          type="button"
          onClick={handlePreview}
          disabled={!canPreview || loading}
          style={{ flex: 1, padding: "10px 0", borderRadius: 8, background: "#f0f0f0", border: "none", fontWeight: 600, color: "#1976d2", cursor: canPreview && !loading ? "pointer" : "not-allowed" }}
        >
          {loading ? "Loading..." : "Preview Contract PDF"}
        </button>
        <button
          type="button"
          onClick={handleSign}
          disabled={!canSign || signing}
          style={{ flex: 1, padding: "10px 0", borderRadius: 8, background: canSign ? "#1976d2" : "#ccc", border: "none", fontWeight: 600, color: "#fff", cursor: canSign && !signing ? "pointer" : "not-allowed" }}
        >
          {signing ? "Signing..." : "Sign & Submit"}
        </button>
      </div>
      {error && <div style={{ color: "#c62828", marginBottom: 8 }}>{error}</div>}
      {previewUrl && (
        <div style={{ marginBottom: 18 }}>
          <div style={{ border: '1px solid #eee', borderRadius: 8, overflow: 'hidden', marginBottom: 6 }}>
            <Document
              file={previewUrl}
              loading={<div style={{ textAlign: 'center', padding: 20 }}>Loading PDF...</div>}
              error={<div style={{ color: '#c62828', textAlign: 'center', padding: 20 }}>Failed to load PDF preview.</div>}
            >
              <Page pageNumber={1} width={400} />
            </Document>
          </div>
          <a href={previewUrl} target="_blank" rel="noopener noreferrer" style={{ color: "#1976d2", fontWeight: 500 }}>Open Unsigned Contract PDF in New Tab</a>
        </div>
      )}
      {signedUrl && (
        <div style={{ marginBottom: 18 }}>
          <div style={{ border: '1px solid #eee', borderRadius: 8, overflow: 'hidden', marginBottom: 6 }}>
            <Document
              file={signedUrl}
              loading={<div style={{ textAlign: 'center', padding: 20 }}>Loading PDF...</div>}
              error={<div style={{ color: '#c62828', textAlign: 'center', padding: 20 }}>Failed to load PDF preview.</div>}
            >
              <Page pageNumber={1} width={400} />
            </Document>
          </div>
          <a href={signedUrl} target="_blank" rel="noopener noreferrer" style={{ color: "#388e3c", fontWeight: 500 }}>Download/View Signed Contract PDF</a>
          <button
            type="button"
            onClick={() => {
              fetch(signedUrl)
                .then(res => res.blob())
                .then(blob => saveAs(blob, `RxRooster-Contract-${groupName}.pdf`));
            }}
            style={{ marginTop: 8, background: '#388e3c', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 600, cursor: 'pointer' }}
          >
            Download Signed PDF
          </button>
        </div>
      )}
      <div style={{ fontSize: 12, color: "#888", marginTop: 12 }}>
        Only authorized representatives may sign. Your signature is legally binding.
      </div>
    </div>
  );
}
